import { format } from "date-fns";
import { DateFormats, UserTypes } from "shared";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { When } from "react-if";

import { Card } from "../../../common/Atoms/Card";
import { CardContent } from "../../../common/Atoms/CardContent";
import { InputTextAddon } from "../../../common/Atoms/InputTextAddon";
import { PhoneNumberInput } from "../../../common/Atoms/PhoneNumberInput";
import { MultiButtonCardHeader } from "../../../common/Atoms/MultiButtonCardHeader";
import { useUser } from "../../hooks/useUser";
import { TwoColFormRow } from "../Molecules/Form/TwoColFormRow";
import { Divider } from "../../../common/Atoms/Divider";
import { useOrganisationsIncludeDisconnected } from "../../hooks/useOrganisationsIncludeDisconnected";
import { Button } from "../../../common/Atoms/Button";
import { useSaveNotificationSettingsMutation, useUpdateUserProfileMutation } from "../../services/api/userApi/user";
import { MultiSelect } from "../../../common/Atoms/MultiSelect";
import { Heading } from "../../../common/Atoms/Typography/Heading";
import { FormDescription } from "../../../common/Atoms/FormDescription";
import { PendingInvitations } from "../Common/PendingInvitations";
import { LoadingOverlay } from "../Molecules/LoadingOverlay";
import { LeaveConfirmationModal } from "../Modals/LeaveConfirmationModal";

import { ConnectedApps } from "./ConnectedApps";

function determineLoginMethod(sub: string) {
  if (sub.toLowerCase().includes(`xero`)) {
    return `Xero`;
  }
  if (sub.toLowerCase().includes(`qbo`)) {
    return `QuickBooks`;
  }

  return `Email`;
}

interface IOrganisationNotificationsState {
  organisationId: string;
  organisationName: string;
  userConnected: boolean;
  weeklyEmail: boolean;
  emailOnMention: boolean;
  emailOnAssignment: boolean;
  emailOnQuoteResponse: boolean;
  emailOnPaymentPlan: boolean;
  emailOnInboundMessages: boolean;
  emailOnPromiseBroken: boolean;
  emailOnQueries: boolean;
}

type NotificationSettingKey = keyof Pick<IOrganisationNotificationsState,
`weeklyEmail`
| `emailOnMention`
| `emailOnAssignment`
| `emailOnQuoteResponse`
| `emailOnPaymentPlan`
| `emailOnInboundMessages`
| `emailOnPromiseBroken`
| `emailOnQueries`
>;

const notificationChoices: Array<{
  label: string;
  value: NotificationSettingKey;
  description: string;
}> = [
  {
    label: `Assignment`,
    value: `emailOnAssignment`,
    description: `Email notification sent when you are assigned to an item such as an escalation or invoice.`,
  },
  {
    label: `Mention`,
    value: `emailOnMention`,
    description: `Email notification sent when you are mentioned in a note.`,
  },
  {
    label: `Quote Response`,
    value: `emailOnQuoteResponse`,
    description: `Email notification sent when a quote is accepted or declined in the portal.`,
  },
  {
    label: `Payment Plan`,
    value: `emailOnPaymentPlan`,
    description: `Email notification sent when a payment plan is created.`,
  },
  {
    label: `Inbound Messages`,
    value: `emailOnInboundMessages`,
    description: `Email notification sent when an inbound SMS or email is received.`,
  },
  {
    label: `Broken Payment Promise`,
    value: `emailOnPromiseBroken`,
    description: `Email notification sent when a customer's payment promise is broken on an invoice assigned to you (or to everyone when unassigned).`,
  },
  {
    label: `Invoice Queries`,
    value: `emailOnQueries`,
    description: `Email notification sent when a customer or the AI agent queries an invoice, or an open query passes its due date, on an invoice assigned to you (or to everyone when unassigned).`,
  },
  {
    label: `Weekly Report`,
    value: `weeklyEmail`,
    description: `Email summary sent weekly for this organisation.`,
  },
];
const notificationChoiceValues = new Set<string>(notificationChoices.map(choice => choice.value));

function isNotificationSettingKey(value: string | number): value is NotificationSettingKey {
  return typeof value === `string` && notificationChoiceValues.has(value);
}

function normaliseNotificationSettings(values: string[] | number[]): NotificationSettingKey[] {
  return values.filter(isNotificationSettingKey);
}

function selectedNotificationSettings(orgNotification: IOrganisationNotificationsState): NotificationSettingKey[] {
  return notificationChoices
    .filter(choice => orgNotification[choice.value])
    .map(choice => choice.value);
}

function notificationDisplayText(selectedValues: NotificationSettingKey[]): string {
  if (!selectedValues.length) {
    return `No email notifications selected`;
  }

  return `${selectedValues.length} of ${notificationChoices.length} selected`;
}

function hasExactSameOrganisationIds(left: Set<string>, right: Set<string>) {
  return left.size === right.size
    && [...left].every(id => right.has(id));
}

export function MyAccount() {
  const { data: user } = useUser();
  const { data: orgs, isLoading } = useOrganisationsIncludeDisconnected();
  const [saveNotifications, { isLoading: saveNotificationsLoading }] = useSaveNotificationSettingsMutation();
  const [ currentNotifications, setCurrentNotifications ] = useState<IOrganisationNotificationsState[]>([]);
  const [ updateUserProfile, { isLoading: updateUserProfileLoading } ] = useUpdateUserProfileMutation();
  const [ firstName, setFirstName ] = useState(``);
  const [ lastName, setLastName ] = useState(``);
  const [ phoneNumber, setPhoneNumber ] = useState(``);
  const notificationSettingsDirty = useRef(false);
  const eligibleOrganisationIds = new Set((user?.orgs || []).filter(orgMembership => (
    [UserTypes.Invited, UserTypes.Inherit].includes(orgMembership.type)
  )).map(orgMembership => orgMembership.id));
  const currentNotificationOrganisationIds = new Set(currentNotifications.map(orgNotification => orgNotification.organisationId));
  const [leaveOrgModal, setLeaveOrgModal] = useState({
    open: false,
    legalName: ``,
    orgId: ``,
  });
  const connectedNotifications = useMemo(
    () => currentNotifications.filter(orgNotification => orgNotification.userConnected),
    [ currentNotifications ],
  );
  const disconnectedNotifications = useMemo(
    () => currentNotifications.filter(orgNotification => !orgNotification.userConnected),
    [ currentNotifications ],
  );

  const disconnectedOrgs = useMemo(() => {
    if (!user?.orgs || !orgs) {
      return [];
    }

    return user.orgs.filter(o => !o.userConnected).map(o => {
      const fullOrg = orgs.find(org => org.id === o.id);

      return {
        id: o.id,
        organisationName: fullOrg ? fullOrg.legalName : o.id,
      };
    });

  }, [user, orgs]);

  useEffect(() => {
    if (!user) {
      setCurrentNotifications([]);
      notificationSettingsDirty.current = false;

      return;
    }

    const eligibleMemberships = user.orgs.filter(orgMembership => (
      [UserTypes.Invited, UserTypes.Inherit].includes(orgMembership.type)
    ));
    const shouldPreserveUnsaved = notificationSettingsDirty.current;
    const orgsById = new Map((orgs || []).map(org => [org.id, org]));

    setCurrentNotifications(prevNotifications => {
      const previousByOrgId = new Map(prevNotifications.map(notification => [notification.organisationId, notification]));

      return eligibleMemberships.map(orgMembership => {
        const organisation = orgsById.get(orgMembership.id);
        const previousNotificationSettings = previousByOrgId.get(orgMembership.id);

        if (shouldPreserveUnsaved && previousNotificationSettings) {
          return {
            ...previousNotificationSettings,
            organisationName: organisation ? organisation.legalName : orgMembership.id,
            userConnected: orgMembership.userConnected,
          };
        }

        return {
          organisationId: orgMembership.id,
          organisationName: organisation ? organisation.legalName : orgMembership.id,
          userConnected: orgMembership.userConnected,
          weeklyEmail: orgMembership.weeklyEmail,
          emailOnMention: orgMembership.emailOnMention,
          emailOnAssignment: orgMembership.emailOnAssignment,
          emailOnQuoteResponse: orgMembership.emailOnQuoteResponse,
          emailOnPaymentPlan: orgMembership.emailOnPaymentPlan,
          emailOnInboundMessages: orgMembership.emailOnInboundMessages,
          emailOnPromiseBroken: orgMembership.emailOnPromiseBroken,
          emailOnQueries: orgMembership.emailOnQueries,
        };
      });
    });

    if (!shouldPreserveUnsaved) {
      notificationSettingsDirty.current = false;
    }
  }, [ user, orgs ]);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || ``);
      setLastName(user.lastName || ``);
      setPhoneNumber(user.phoneNumber || ``);
    }
  }, [ user ]);

  function handleNotificationSelection(
    organisationId: string,
    values: string[] | number[],
  ) {
    const selectedValues = new Set<NotificationSettingKey>(normaliseNotificationSettings(values));
    notificationSettingsDirty.current = true;

    setCurrentNotifications(prev => prev.map(orgNotification => {
      if (orgNotification.organisationId !== organisationId) {
        return orgNotification;
      }

      return {
        ...orgNotification,
        emailOnAssignment: selectedValues.has(`emailOnAssignment`),
        emailOnMention: selectedValues.has(`emailOnMention`),
        emailOnQuoteResponse: selectedValues.has(`emailOnQuoteResponse`),
        emailOnPaymentPlan: selectedValues.has(`emailOnPaymentPlan`),
        emailOnInboundMessages: selectedValues.has(`emailOnInboundMessages`),
        emailOnPromiseBroken: selectedValues.has(`emailOnPromiseBroken`),
        emailOnQueries: selectedValues.has(`emailOnQueries`),
        weeklyEmail: selectedValues.has(`weeklyEmail`),
      };
    }));
  }

  async function handleSaveNotifications() {
    if (!user) return;

    await saveNotifications({
      organisations: currentNotifications.map(orgNotification => ({
        organisationId: orgNotification.organisationId,
        weeklyEmail: orgNotification.weeklyEmail,
        emailOnMention: orgNotification.emailOnMention,
        emailOnAssignment: orgNotification.emailOnAssignment,
        emailOnQuoteResponse: orgNotification.emailOnQuoteResponse,
        emailOnPaymentPlan: orgNotification.emailOnPaymentPlan,
        emailOnInboundMessages: orgNotification.emailOnInboundMessages,
        emailOnPromiseBroken: orgNotification.emailOnPromiseBroken,
        emailOnQueries: orgNotification.emailOnQueries,
      })),
    }).unwrap();

    notificationSettingsDirty.current = false;

    return;
  }

  async function handleSaveProfile() {
    if (!user) return;

    await updateUserProfile({
      first_name: firstName,
      last_name: lastName,
      phone_number: phoneNumber,
    });

    return;
  }

  function onRemoveOrg(orgId: string, orgName: string) {
    // Simply open the leave org modal
    setLeaveOrgModal({
      open: true,
      legalName: orgName,
      orgId,
    });
  }

  function onCancelRemoveOrg() {
    setLeaveOrgModal({
      open: false,
      legalName: ``,
      orgId: ``,
    });
  }

  if (!user) {
    return null;
  }

  const renderOrganisationNotificationRow = (orgNotification: IOrganisationNotificationsState) => {
    const selectedValues = selectedNotificationSettings(orgNotification);

    return (
      <div
        key={ orgNotification.organisationId }
        className={ `grid gap-3 p-4 md:grid-cols-[minmax(0,1fr)_minmax(280px,340px)] md:items-start` }
      >
        <div className={ `space-y-1` }>
          <Heading>
            { orgNotification.userConnected
              ? orgNotification.organisationName
              : `${orgNotification.organisationName} (Disconnected)` }
          </Heading>
          <FormDescription>
            { orgNotification.userConnected
              ? `Choose which email notifications you receive for this organisation.`
              : `Preferences apply when you reconnect. No emails are sent while this organisation is disconnected.` }
          </FormDescription>
        </div>
        <MultiSelect
          options={ notificationChoices }
          selected={ selectedValues }
          display={ notificationDisplayText(selectedValues) }
          onChange={ values => handleNotificationSelection(orgNotification.organisationId, values) }
          ariaLabel={ `Email notifications for ${orgNotification.organisationName}` }
        />
      </div>
    );
  };

  return (
    <Card>
      <LeaveConfirmationModal
        open={ leaveOrgModal.open }
        setOpen={ onCancelRemoveOrg }
        orgName={ leaveOrgModal.legalName }
        orgId={ leaveOrgModal.orgId }
      />
      <MultiButtonCardHeader
        title={ `My Account` }
      />
      <CardContent className={ `mt-4` }>
        <TwoColFormRow
          heading={ `Personal Information` }
        >
          <LoadingOverlay
            loading={ updateUserProfileLoading }
          >
            <div
              className={ `space-y-3` }>

              <div
                className={ `flex items-end justify-between w-full` }
              >
                <InputTextAddon
                  className={ `w-1/2 mr-6` }
                  label={ `First Name` }
                  value={ firstName }
                  disabled={ false }
                  onChange={ e => setFirstName(e.value as string) }
                />

                <InputTextAddon
                  className={ `w-1/2` }
                  label={ `Last Name` }
                  value={ lastName }
                  disabled={ false }
                  onChange={ e => setLastName(e.value as string) }
                />
              </div>

              <PhoneNumberInput
                className={ `w-1/2` }
                label={ `Phone Number` }
                name={ `phoneNumber` }
                value={ phoneNumber }
                subHeading={ `Used for click to call: we ring this number first, then connect you to the customer.` }
                placeholder={ `eg. 412 345 678` }
                onChange={ e => setPhoneNumber(e.value) }
              />

              <div className={ `flex justify-end` }>
                <Button
                  loading={ updateUserProfileLoading }
                  loadingText={ `Saving...` }
                  onClick={ handleSaveProfile }
                >
                  { `Update` }
                </Button>
              </div>

              <div>
                <InputTextAddon
                  label={ `Email` }
                  value={ user.email }
                  disabled={ true }
                  onChange={ () => {} }
                />
              </div>

              <div className={ `flex items-end justify-between w-full space-x-3` }>
                <InputTextAddon
                  label={ `First Signed Up` }
                  value={ format(new Date(user.createdAt), DateFormats.UI_DISPLAY_SHORT) }
                  disabled={ true }
                  onChange={ () => {} }
                />

                <InputTextAddon
                  label={ `Login Count` }
                  value={ user.loginsCount }
                  disabled={ true }
                  onChange={ () => {} }
                />

                <InputTextAddon
                  label={ `Login Method` }
                  value={ determineLoginMethod(user.sub) }
                  disabled={ true }
                  onChange={ () => {} }
                />
              </div>
            </div>
          </LoadingOverlay>
        </TwoColFormRow>
        <Divider/>
        <TwoColFormRow
          heading={ `Email Notifications` }
        >
          <LoadingOverlay
            loading={ isLoading || saveNotificationsLoading }
          >
            <div
              className={ `space-y-3 mb-4` }
            >

              <div className={ `divide-y divide-gray-200 rounded-md border border-gray-200` }>
                { connectedNotifications.map(renderOrganisationNotificationRow) }
                {
                  disconnectedNotifications.length > 0 &&
                  <Disclosure as={ `div` }>
                    {
                      ({ open }) => (
                        <>
                          <DisclosureButton className={ `flex w-full items-center justify-between gap-2 bg-gray-50 px-4 py-3 text-left text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-paidniceGreen-400` }>
                            <span>{ `Disconnected organisations (${disconnectedNotifications.length})` }</span>
                            <ChevronDownIcon className={ `h-4 w-4 text-gray-500 transition-transform duration-200 ${open ? `rotate-180` : ``}` } />
                          </DisclosureButton>
                          <DisclosurePanel className={ `divide-y divide-gray-200` }>
                            { disconnectedNotifications.map(renderOrganisationNotificationRow) }
                          </DisclosurePanel>
                        </>
                      )
                    }
                  </Disclosure>
                }
              </div>

            </div>
          </LoadingOverlay>
        </TwoColFormRow>
        <div className={ `mb-6` } />
        <div className={ `flex justify-end` }>
          <Button
            disabled={
              saveNotificationsLoading
              || !hasExactSameOrganisationIds(currentNotificationOrganisationIds, eligibleOrganisationIds)
            }
            loading={ saveNotificationsLoading }
            loadingText={ `Saving...` }
            onClick={ handleSaveNotifications }
          >
            { `Save Notifications` }
          </Button>
        </div>

        <Divider/>

        <ConnectedApps />

        <Divider/>

        <TwoColFormRow
          heading={ `Invitations` }
        >
          <div
            className={ `space-y-3` }>

            <PendingInvitations showContentOnly={ true } />
          </div>
        </TwoColFormRow>

        { /* When user is disconnected to any org, allow them to remove it */ }
        <When condition={ disconnectedOrgs.length > 0 }>
          <Divider/>
          <TwoColFormRow
            heading={ `Disconnected Organisations` }
            description={ `Organisations you have lost access to and can be removed from your account.` }
          >
            <div
              className={ `space-y-3` }>

              {
                disconnectedOrgs.map(org => (
                  <div
                    key={ org.id }
                    className={ `flex items-center justify-between w-full` }
                  >
                    <span>{ org.organisationName }</span>
                    <Button
                      color={ `red` }
                      onClick={ () => onRemoveOrg(org.id, org.organisationName) }
                    >
                      { `Remove` }
                    </Button>
                  </div>
                ))
              }
            </div>
          </TwoColFormRow>
        </When>
      </CardContent>
    </Card>
  );
}
